import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "next-i18next";
import throttle from "lodash/throttle";
import bridge from "@/utils/dsBridge";
import { BridgeCallHandlers } from "@/interfaces/enums";
import useInApp from "./useIsApp";

export default function usePolicyScroll() {
	const { t } = useTranslation("policy");
	const router = useRouter();
	const isInApp = useInApp();

	const containerRef = useRef<any>(null);
	const [btnText, setBtnText] = useState(t("review"));
	const [scrollEnd, setScrollEnd] = useState(false);

	const handleScrollReducer = () => {
		if (scrollEnd || !containerRef) {
			return;
		}

		const safeDistance = 40;
		const currentScroll = containerRef.current.scrollTop;
		const clientHeight = containerRef.current.clientHeight;
		const scrollHeight = containerRef.current.scrollHeight;
		const isScrollEnd = scrollHeight < currentScroll + clientHeight + safeDistance;

		if (isScrollEnd) {
			setScrollEnd(true);
			setBtnText(t("review-finished"));
		}
	};

	useEffect(() => {
		handleScrollReducer();

		const handleStaticScroll = throttle(handleScrollReducer, 300);
		const container = containerRef.current;

		container.addEventListener("scroll", handleStaticScroll);
		return () => {
			container.removeEventListener("scroll", handleStaticScroll);
		};
	}, []);

	const smoothScroll = () => {
		const step = 10;
		const surplus = 20;
		const currentScroll = containerRef.current.scrollTop;
		const clientHeight = containerRef.current.clientHeight;
		const scrollHeight = containerRef.current.scrollHeight;

		if (scrollHeight - surplus > currentScroll + clientHeight) {
			window.requestAnimationFrame(smoothScroll);
			containerRef.current.scrollTo(0, currentScroll + (scrollHeight - currentScroll - clientHeight) / step);
		}
	};

	const handleRead = () => {
		if (scrollEnd) {
			if (isInApp) {
				bridge.call(BridgeCallHandlers.ON_CLOSE_WEB_PAGE);
				return;
			}

			router.back();
			return;
		}
		if (!containerRef.current) {
			return;
		}
		smoothScroll();
	};
	return { btnText, containerRef, handleRead };
}
